import { Menu, MenuItem, Tooltip } from '@material-ui/core';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { RegisterDeviceContracts, verifyPatientMedicareCard } from '../Async/PatientMedicareCardVerify';
import { SearchDeviceContracts } from '../Async/SearchDevicesAsync';
import { Button, ButtonMode, ButtonSize } from '../Components/Button';
import { ButtonGroup } from '../Components/ButtonGroup';
import { StyledDialog } from '../Components/Dialog/StyledDialog';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { StyledDialogContent } from '../Components/Dialog/StyledDialogContent';
import { ArrowDropDownIcon, ArrowDropRightIcon, DuplicateIcon, UserCheckedIcon } from '../Components/Icons';
import { LayoutCentral } from '../Components/Layout/LayoutCentral';
import { MaskedTextField } from '../Components/MaskedTextField';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { isSingleSelectSingleValue, StyledSelect } from '../Components/StyledSelect';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { isDomainValidationError } from '../Shared/DomainValidationFunctions';
import { isHttpResponse } from '../Shared/HttpRequestExecutor';
import { Reference } from '../Shared/SharedModels';
import { sentenceCase } from '../Shared/StringUtilities';
import { Nullable } from '../Shared/Types';
import { white } from '../Styles/Colours';
import styles from './OPVCheck.module.scss';

interface OPVCheckerProps {
    device: SearchDeviceContracts.Response.DevicesSearchResult;
}

interface OPVCheckerDialogProps extends OPVCheckerProps {
    onClose: () => void;
}

enum CheckStatus {
    Init,
    Checking,
    Checked
}

type PatientInfo = {
    dateOfBirth: string;
    familyName: string;
    givenName: string;
    sex: Nullable<Reference>;
    medicareCardNumber: string;
    medicareCardReferenceNumber: string;
};

const meteorSexCode = [
    {
        code: '1',
        name: 'Male',
        codingSystemUri: 'http://telstrahealth/terminology/vs/meteorsex'
    },
    {
        code: '2',
        name: 'Female',
        codingSystemUri: 'http://telstrahealth/terminology/vs/meteorsex'
    },
    {
        code: '3',
        name: 'Other',
        codingSystemUri: 'http://telstrahealth/terminology/vs/meteorsex'
    },
    {
        code: '9',
        name: 'Not stated/inadequately described',
        codingSystemUri: 'http://telstrahealth/terminology/vs/meteorsex'
    }
];

const clearFields = {
    minorId: '',
    patient: {
        dateOfBirth: '',
        familyName: '',
        givenName: '',
        sex: null,
        medicareCardNumber: '',
        medicareCardReferenceNumber: ''
    }
};
const patients: {
    type: string;
    patient: PatientInfo;
}[] = [
    {
        type: 'Valid',
        patient: {
            dateOfBirth: '1972-04-03',
            familyName: 'Baird',
            givenName: 'Lucas',
            sex: meteorSexCode[0],
            medicareCardNumber: '2953876021',
            medicareCardReferenceNumber: '1'
        }
    },
    {
        type: 'Invalid',
        patient: {
            dateOfBirth: '1979-07-07',
            familyName: 'Willis',
            givenName: 'Bob',
            sex: meteorSexCode[0],
            medicareCardNumber: '2953876021',
            medicareCardReferenceNumber: '1'
        }
    },
    {
        type: 'Valid with suggestions',
        patient: {
            dateOfBirth: '1972-04-03',
            familyName: 'Baird',
            givenName: 'Robert',
            sex: meteorSexCode[0],
            medicareCardNumber: '2953876021',
            medicareCardReferenceNumber: '1'
        }
    },
    {
        type: 'Valid with returned card',
        patient: {
            dateOfBirth: '2000-09-12',
            familyName: 'Harris',
            givenName: 'Sam',
            sex: meteorSexCode[0],
            medicareCardNumber: '6951393261',
            medicareCardReferenceNumber: '3'
        }
    }
];

const defaultsFields: {
    minorId: string;
    patient: PatientInfo;
} = {
    minorId: 'TEH00000',
    patient: patients[0].patient
};

function useOPVState(props: OPVCheckerDialogProps) {
    const [status, setStatus] = useState<CheckStatus>(CheckStatus.Init);
    const [errorText, setErrorText] = useState<string>('');
    const [messageText, setMessageText] = useState<string>('');

    const [timeStamp, setTimeStamp] = useState(moment().format(ValidDateFormats.NodeDateTimeTimezone));

    function updateTimeStamp() {
        setTimeStamp(moment().format(ValidDateFormats.NodeDateTimeTimezone));
    }

    const [minorId, setMinorId] = useState(defaultsFields.minorId);
    function updateMinorId(minorId: string) {
        updateTimeStamp();
        setMinorId(minorId);
    }

    const [patient, setPatient] = useState<PatientInfo>(defaultsFields.patient);
    function updatePatientField(field: keyof PatientInfo, value: string | Nullable<Reference>) {
        updateTimeStamp();
        setPatient(patient => ({
            ...patient,
            [field]: value
        }));
    }

    function clearAllFields() {
        updateTimeStamp();
        setMinorId(clearFields.minorId);
        setPatient(patient => ({
            ...patient,
            ...clearFields.patient
        }));
    }

    function useExample(newPatient: PatientInfo) {
        updateTimeStamp();
        setMinorId(defaultsFields.minorId);
        setPatient(patient => ({
            ...patient,
            ...newPatient
        }));
    }

    const payload: RegisterDeviceContracts.Request.MedicareCardVerification = {
        minorId,
        authoringSystem: props.device.authoringSystem,
        authoringSystemForeignKey: `opv-check-${timeStamp}`,
        device: {
            authoringSystem: props.device.authoringSystem,
            authoringSystemForeignKey: props.device.authoringSystemForeignKey
        },
        patient: {
            authoringSystem: props.device.authoringSystem,
            authoringSystemForeignKey: `patient-${timeStamp}`,
            ...patient
        }
    };

    async function runCheck() {
        setErrorText('');
        setStatus(CheckStatus.Checking);
        setMessageText('');

        try {
            const resp = await verifyPatientMedicareCard(payload);
            setStatus(CheckStatus.Checked);

            if (resp) {
                setMessageText(prettyPrintJson(resp));
            } else {
                setErrorText('Unknown error occurred');
            }
        } catch (e: any) {
            setStatus(CheckStatus.Checked);

            if (isDomainValidationError(e)) {
                setErrorText(prettyPrintJson(e));
            } else if (isHttpResponse(e)) {
                let errorResponse = await e.json();
                setErrorText(prettyPrintJson(errorResponse));
            }
        }
    }

    return {
        status,
        errorText,
        messageText,
        payload,
        clearAllFields,
        useExample,
        updateMinorId,
        runCheck,
        updatePatientField
    };
}

function syntaxHighlight(json: string): string {
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        function (match) {
            var cls = styles.number;
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cls = styles.key;
                } else {
                    cls = styles.string;
                }
            } else if (/true|false/.test(match)) {
                cls = styles.boolean;
            } else if (/null/.test(match)) {
                cls = styles.null;
            }
            return '<span class="' + cls + '">' + match + '</span>';
        }
    );
}

function JSONFormatter(props: { json: string }) {
    return (
        <div className={styles.jsonFormatter}>
            <pre
                dangerouslySetInnerHTML={{
                    __html: syntaxHighlight(props.json)
                }}
            ></pre>
        </div>
    );
}

function prettyPrintJson(jsonData: any) {
    return JSON.stringify(jsonData, null, 4);
}

function Section(props: {
    title: string;
    startClosed?: boolean;
    children?: React.ReactNode;
    error?: boolean;
    copyText?: string;
}) {
    const [showSection, setShowSection] = useState(!props.startClosed);

    function copyToClipboard(e: React.MouseEvent) {
        e.stopPropagation();
        if (props.copyText) window.navigator.clipboard.writeText(props.copyText);
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader} onClick={() => setShowSection(shouldShow => !shouldShow)}>
                <div className={styles.sectionTitle}>
                    {showSection ? <ArrowDropDownIcon color={white} /> : <ArrowDropRightIcon color={white} />}
                    {props.title}
                </div>
                <div className={styles.spacer} />
                {props.copyText && (
                    <div className={styles.sectionCopyDeadZone} onClick={e => e.stopPropagation()}>
                        <Tooltip title='Copy'>
                            <div className={styles.sectionCopyButton} onClick={copyToClipboard}>
                                <DuplicateIcon color={white} />
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>

            {showSection && (
                <>
                    {props.error && <div className={styles.sectionError}>Error occurred</div>}
                    <div className={styles.sectionBody}>{props.children}</div>
                </>
            )}
        </div>
    );
}

function PatientSelectorButton(props: { onPatientPick: (patient: PatientInfo) => void }) {
    const [anchorEl, setAnchorEl] = React.useState<Nullable<HTMLButtonElement>>(null);
    const open = Boolean(anchorEl);

    const handleClose = () => {
        setAnchorEl(null);
    };

    function baseClick() {
        props.onPatientPick(patients[0].patient);
    }

    return (
        <>
            <ButtonGroup>
                <Button testId='use-example' onClick={baseClick}>
                    Example
                </Button>
                <Button
                    testId='use-dropdown'
                    onClick={event => {
                        setAnchorEl(event.currentTarget);
                    }}
                    width={40}
                >
                    <ArrowDropDownIcon color={white} />
                </Button>
            </ButtonGroup>
            <Menu
                id='basic-menu'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button'
                }}
            >
                {patients.map(patientInfo => {
                    const patientName = sentenceCase(
                        `${patientInfo.patient.givenName} ${patientInfo.patient.familyName}`
                    );
                    return (
                        <MenuItem
                            key={patientInfo.type}
                            onClick={() => {
                                handleClose();
                                props.onPatientPick(patientInfo.patient);
                            }}
                        >
                            {patientInfo.type} - ({patientName})
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

function OPVCheckerDialog(props: OPVCheckerDialogProps): JSX.Element {
    const opvState = useOPVState(props);
    return (
        <StyledDialog
            size='lg'
            open
            title='OPV Check'
            closeFunction={props.onClose}
            actions={[
                <StyledDialogButton
                    title='Close'
                    key='Check-device-token-dialog-props'
                    onClick={opvState.runCheck}
                    mode={ButtonMode.solid}
                    width={112}
                    testId='deviceTokenTester-Check-dialog'
                >
                    Check
                </StyledDialogButton>,
                <StyledDialogButton
                    title='Close'
                    key='close-device-token-dialog-props'
                    onClick={props.onClose}
                    mode={ButtonMode.border}
                    width={112}
                    testId='deviceTokenTester-close-dialog'
                >
                    Close
                </StyledDialogButton>
            ]}
            contentComponent={
                <StyledDialogContent noPadding>
                    <LayoutCentral>
                        <div className={styles.opvCheckLayout}>
                            <div className={styles.formSection}>
                                <div className={styles.inputFields}>
                                    <MaskedTextField
                                        value={opvState.payload.patient.familyName}
                                        label='Family name'
                                        testId='familyName'
                                        onChange={value => opvState.updatePatientField('familyName', value)}
                                    />
                                    <MaskedTextField
                                        value={opvState.payload.patient.givenName}
                                        label='Given name'
                                        testId='givenName'
                                        onChange={value => opvState.updatePatientField('givenName', value)}
                                    />
                                    <MaskedTextField
                                        value={opvState.payload.patient.medicareCardNumber}
                                        label='Medicare card number'
                                        testId='medicareCardNumber'
                                        onChange={value => opvState.updatePatientField('medicareCardNumber', value)}
                                    />
                                    <MaskedTextField
                                        value={opvState.payload.patient.medicareCardReferenceNumber}
                                        label='Medicare card reference number'
                                        testId='medicareCardReferenceNumber'
                                        onChange={value =>
                                            opvState.updatePatientField('medicareCardReferenceNumber', value)
                                        }
                                    />
                                    <MaskedTextField
                                        value={opvState.payload.patient.dateOfBirth}
                                        label='Date of birth'
                                        testId='dateOfBirth'
                                        type='date'
                                        onChange={value => {
                                            opvState.updatePatientField('dateOfBirth', value);
                                        }}
                                    />
                                    <MaskedTextField
                                        value={opvState.payload.minorId}
                                        label='Minor Id'
                                        testId='minorId'
                                        onChange={value => opvState.updateMinorId(value)}
                                    />
                                    <div className={styles.inputField}>
                                        <StyledSelect<Reference>
                                            label='Sex'
                                            testId='sex'
                                            value={opvState.payload.patient.sex}
                                            onSelection={selection => {
                                                if (isSingleSelectSingleValue(selection))
                                                    opvState.updatePatientField('sex', selection);
                                                else opvState.updatePatientField('sex', null);
                                            }}
                                            isClearable
                                            itemToMenuKey={suggestion => suggestion.code}
                                            itemToString={suggestion => suggestion.name}
                                            maxResults={0}
                                            suggestions={meteorSexCode}
                                        />
                                    </div>
                                </div>
                                <div className={styles.buttonContainer}>
                                    <Button
                                        testId='clear-all'
                                        onClick={opvState.clearAllFields}
                                        mode={ButtonMode.border}
                                    >
                                        Clear all fields
                                    </Button>
                                    <PatientSelectorButton onPatientPick={opvState.useExample} />
                                </div>
                            </div>
                            <div className={styles.displaySection}>
                                <Section title='Outbound' startClosed copyText={prettyPrintJson(opvState.payload)}>
                                    <JSONFormatter json={prettyPrintJson(opvState.payload)} />
                                </Section>
                                <Section
                                    title='Response'
                                    error={!!opvState.errorText}
                                    copyText={opvState.errorText ? opvState.errorText : opvState.messageText}
                                >
                                    {opvState.status === CheckStatus.Init ||
                                    opvState.status === CheckStatus.Checking ? (
                                        <div className={styles.noCheckRunInfo}>
                                            {opvState.status === CheckStatus.Checking ? (
                                                <LayoutCentral>
                                                    <LoadingPlaceholder />
                                                </LayoutCentral>
                                            ) : (
                                                <>
                                                    <span>Check not run</span>
                                                    <Button
                                                        title='Close'
                                                        key='Check-device-token-dialog-props'
                                                        onClick={opvState.runCheck}
                                                        mode={ButtonMode.solid}
                                                        width={112}
                                                        testId='deviceTokenTester-Check-dialog-nag'
                                                    >
                                                        Check
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    ) : opvState.errorText ? (
                                        <JSONFormatter json={opvState.errorText} />
                                    ) : (
                                        <JSONFormatter json={opvState.messageText} />
                                    )}
                                </Section>
                            </div>
                        </div>
                    </LayoutCentral>
                </StyledDialogContent>
            }
        />
    );
}

export function OPVCheck(props: OPVCheckerProps): JSX.Element {
    const [showDialog, setShowDialog] = useState<boolean>(false);

    function iconClick(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        setShowDialog(true);
    }

    function dialogClose() {
        setShowDialog(false);
    }

    return (
        <>
            <Tooltip title='Run OPV Check'>
                <span>
                    <UserCheckedIcon size='40' className={styles.icon} onClick={iconClick} />
                </span>
            </Tooltip>
            {showDialog && <OPVCheckerDialog device={props.device} onClose={dialogClose} />}
        </>
    );
}
